import { ExternalLink } from "lucide-react";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./vendor-info.i18n";

/** The kinds of vendor resource links we surface, in render order. */
export type LinkKind = "terms" | "status" | "security" | "documentation";

/** A verified external link to one of the vendor's public pages. */
export type VendorLink = { kind: LinkKind; href: string };

/** Identity of a 3rd-party sub-processor that delivers a feature or AI model. */
export type Vendor = {
  /** Registered legal entity name, e.g. "Trapets AB". */
  name: string;
  /** Main homepage; when set, the name renders as a link to it. */
  homepage?: string;
  /** Identity lines (registration number, VAT, registered address). */
  details?: string[];
  /** Verified links to the vendor's terms / status / security / docs pages (omit any that don't exist). */
  links?: VendorLink[];
};

/**
 * The sub-processor identity block: who actually delivers a feature, with the legal
 * entity (name linked to its homepage), optional registration/address details, and
 * verified terms/status/security/docs links. Shared by the 3rd-party store cards and
 * the AI Settings page so vendor disclosure looks identical everywhere.
 */
export function VendorInfo({ vendor }: { vendor: Vendor }) {
  const ll = useT(T);

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">
        {ll.subProcessor}:{" "}
        {vendor.homepage ? (
          <a
            href={vendor.homepage}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-foreground hover:underline"
          >
            {vendor.name}
          </a>
        ) : (
          <span className="font-medium text-foreground">{vendor.name}</span>
        )}
      </p>
      {vendor.details && vendor.details.length > 0 && (
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {vendor.details.join(" · ")}
        </p>
      )}
      {vendor.links && vendor.links.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-0.5">
          {vendor.links.map((l) => (
            <a
              key={l.kind}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              {ll[l.kind]}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
